import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const NotificationDeliverySettings = ({ settings, onUpdate }) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const [hasChanges, setHasChanges] = useState(false);

  const handleDeliveryMethodChange = (method, checked) => {
    setLocalSettings(prev => ({
      ...prev,
      [method]: checked
    }));
    setHasChanges(true);
  };

  const handleQuietHoursChange = (field, value) => {
    setLocalSettings(prev => ({
      ...prev,
      quietHours: {
        ...prev?.quietHours,
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(localSettings);
    setHasChanges(false);
  };

  const handleReset = () => {
    setLocalSettings(settings);
    setHasChanges(false);
  };

  const deliveryMethods = [
    {
      key: 'email',
      label: 'Email Notifications',
      description: 'Receive alerts via email',
      icon: 'Mail'
    },
    {
      key: 'sms',
      label: 'SMS Notifications',
      description: 'Receive critical alerts via SMS',
      icon: 'MessageSquare'
    },
    {
      key: 'slack',
      label: 'Slack Integration',
      description: 'Send alerts to Slack channel',
      icon: 'Hash'
    },
    {
      key: 'inApp',
      label: 'In-App Notifications',
      description: 'Show browser notifications',
      icon: 'Bell'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="bg-muted/50 px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="Send" size={20} className="text-primary" strokeWidth={2} />
            <h2 className="text-lg font-semibold text-foreground">Notification Delivery</h2>
          </div>
          {hasChanges && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Reset
              </button>
              <Button onClick={handleSave} size="sm" iconName="Save">
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Delivery Methods */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Delivery Channels</h3>
          {deliveryMethods?.map((method) => (
            <div key={method?.key} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={method?.icon} size={20} className="text-primary" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <Checkbox
                  checked={localSettings?.[method?.key]}
                  onChange={(e) => handleDeliveryMethodChange(method?.key, e?.target?.checked)}
                  label={method?.label}
                  description={method?.description}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Quiet Hours */}
        <div className="space-y-4 pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Quiet Hours</h3>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings?.quietHours?.enabled}
                onChange={(e) => handleQuietHoursChange('enabled', e?.target?.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
          
          {localSettings?.quietHours?.enabled && (
            <div className="space-y-3 pl-4 border-l-2 border-border">
              <p className="text-xs text-muted-foreground">
                Suppress non-critical notifications during specified hours
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-foreground mb-1 block">Start Time</label>
                  <input
                    type="time"
                    value={localSettings?.quietHours?.start}
                    onChange={(e) => handleQuietHoursChange('start', e?.target?.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground mb-1 block">End Time</label>
                  <input
                    type="time"
                    value={localSettings?.quietHours?.end}
                    onChange={(e) => handleQuietHoursChange('end', e?.target?.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Role-Based Routing */}
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Users" size={20} className="text-warning flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">Role-Based Routing</h4>
            <p className="text-xs text-muted-foreground">
              Critical alerts are automatically routed to admin roles. Configure additional routing rules in Notification Management.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationDeliverySettings;